import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'

import {
  getChartModeTypes,
  getDefaultChartMode
} from '@aytm/app/stats/page/question-views/oe/components/chart/lib/chartModes'

const OpenEndedChart = ({
  questionId,
  questionKey,
  qType,
  answerVariants,
  answer,
  sortOrder,
  chartMode: chartModeProp,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  zoomType,
  isTraitType,
  format,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features
}) => {
  const percentsOnly = format?.percentsOnly === true
  const amountOnly = format?.amountOnly === true

  const chartModeTypes = useMemo(() => getChartModeTypes({ answer, percentsOnly }), [answer, percentsOnly])

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        globalChartMode,
        answer,
        percentsOnly
      }),
    [chartModeProp, globalChartMode, answer, percentsOnly]
  )

  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <ChartContainer
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      answerVariants={answerVariants}
      answer={answer}
      sortOrder={sortOrder}
      chartMode={chartMode}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={viewOptionsDisabled}
      onChartModeChange={onChartModeChange}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      zoomType={zoomType && chartMode !== 'pie'}
      onZoomChange={onZoomChange}
      ignoreTraits={format?.ignoreTraits === true || !isTraitType}
      ignoreRefs={format?.ignoreRefs === true}
      ignoreAmount={percentsOnly}
      ignorePercents={amountOnly}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      format={format}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      features={features}
    />
  )
}

OpenEndedChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  answerVariants: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  chartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  // zoom arrives from the report params and the page-level setting, so it is a token
  // ('max', a stored string) as often as a number -- same union the sibling charts declare
  zoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onZoomChange: PropTypes.func,
  zoomType: PropTypes.bool,
  isTraitType: PropTypes.bool,
  format: PropTypes.object,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object
}

OpenEndedChart.displayName = 'OpenEndedChart'

export default OpenEndedChart
